import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquare,
  Code,
  Layers,
  CheckCircle2,
  ArrowRight,
  Bot,
  Terminal,
  ExternalLink,
  ShieldCheck,
  Zap,
  Send,
  Loader2,
  Trash2,
  Sparkles,
  Copy,
  Check,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useChatbot } from "../../hooks/useChatbot";

const ADOPTION_ROUTES = [
  {
    id: "chatbot",
    title: "Agentic Copilot (Chatbot)",
    icon: MessageSquare,
    color: "text-teal-600",
    bg: "bg-teal-50",
    description: "Natural language discovery and management of grants through an intelligent assistant.",
    features: ["Natural language queries", "Qwen3-235B powered", "Context-aware", "Session memory"],
    status: "Live",
  },
  {
    id: "api",
    title: "Direct API Integration",
    icon: Code,
    color: "text-blue-600",
    bg: "bg-blue-50",
    description: "Deeply integrate Impact Plexus matching engine into your existing CRM or Grant Management System.",
    features: ["RESTful endpoints", "Real-time matching", "Webhooks for updates"],
    status: "Production Ready",
  },
  {
    id: "whitelabel",
    title: "White-label Frontend",
    icon: Layers,
    color: "text-purple-600",
    bg: "bg-purple-50",
    description: "Deploy a fully branded version of the Impact Plexus dashboard on your own domain.",
    features: ["Custom CSS/Branding", "Your own domain", "Managed hosting"],
    status: "Beta",
  },
];

const QUICK_PROMPTS = [
  "What grants are available for homeless youth programs in Ontario?",
  "How does the FundMatch scoring work?",
  "Help me prepare a grant application",
  "What's the difference between federal and provincial grants?",
];

function MarkdownLight({ text }) {
  // Minimal markdown: bold, bullet points, numbered lists
  const lines = text.split('\n');
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} className="h-1.5" />;
        // Bold
        let html = trimmed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        // Bullet
        if (/^[-•]\s/.test(trimmed)) {
          html = html.replace(/^[-•]\s/, '');
          return <div key={i} className="flex gap-2 ml-2"><span className="text-teal-500 mt-0.5">•</span><span dangerouslySetInnerHTML={{ __html: html }} /></div>;
        }
        // Numbered
        const numMatch = trimmed.match(/^(\d+)\.\s/);
        if (numMatch) {
          html = html.replace(/^\d+\.\s/, '');
          return <div key={i} className="flex gap-2 ml-2"><span className="text-teal-500 font-bold">{numMatch[1]}.</span><span dangerouslySetInnerHTML={{ __html: html }} /></div>;
        }
        return <p key={i} dangerouslySetInnerHTML={{ __html: html }} />;
      })}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-zinc-100"
      title="Copy"
    >
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3 text-zinc-400" />}
    </button>
  );
}

export const AdoptionTab = ({ charityBN }) => {
  const [activeRoute, setActiveRoute] = useState("chatbot");
  const [input, setInput] = useState("");
  const { messages, isLoading, error, sendMessage, clearChat } = useChatbot(charityBN);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;
    sendMessage(input);
    setInput("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="space-y-5" data-testid="adoption-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900">Adoption Routes</h1>
          <p className="text-sm text-zinc-500">Deploy and integrate Impact Plexus into your organization.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="px-3 py-1">v8.7</Badge>
          <Badge className="bg-teal-100 text-teal-700 px-3 py-1">
            <Sparkles className="h-3 w-3 mr-1 inline" />Copilot Live
          </Badge>
        </div>
      </div>

      {/* Route cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {ADOPTION_ROUTES.map((route) => {
          const Icon = route.icon;
          const isActive = activeRoute === route.id;
          return (
            <Card
              key={route.id}
              className={`cursor-pointer transition-all border-2 ${
                isActive ? "border-teal-500 shadow-md bg-teal-50/30" : "border-zinc-200 hover:border-teal-200"
              }`}
              onClick={() => setActiveRoute(route.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-9 h-9 rounded-lg ${route.bg} flex items-center justify-center ${route.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <CardTitle className="text-xs font-bold">{route.title}</CardTitle>
                    <Badge
                      variant="outline"
                      className={`text-[9px] px-1.5 py-0 h-3.5 ${route.status === "Live" ? "bg-green-50 text-green-700 border-green-300" : ""}`}
                    >
                      {route.status}
                    </Badge>
                  </div>
                </div>
                <p className="text-[10px] text-zinc-500 line-clamp-1">{route.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Content area */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeRoute}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.15 }}
        >
          {activeRoute === "chatbot" && (
            <div className="grid lg:grid-cols-4 gap-5">
              {/* Chat interface */}
              <Card className="lg:col-span-3 overflow-hidden border-zinc-200">
                <CardHeader className="bg-gradient-to-r from-zinc-50 to-teal-50/50 border-b py-2.5 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-xs font-semibold text-zinc-700">
                        Impact Plexus Copilot
                      </span>
                      <Badge className="bg-zinc-100 text-zinc-500 text-[9px] px-1.5 h-4">Qwen3-235B</Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearChat}
                      className="h-6 px-2 text-[10px] text-zinc-400 hover:text-red-500"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />Clear
                    </Button>
                  </div>
                </CardHeader>

                <CardContent className="p-0 flex flex-col" style={{ height: "520px" }}>
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex gap-3 group ${msg.role === "user" ? "justify-end" : ""}`}
                      >
                        {msg.role === "assistant" && (
                          <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center shrink-0 mt-0.5">
                            <Bot className="h-3.5 w-3.5 text-teal-600" />
                          </div>
                        )}
                        <div
                          className={`max-w-[80%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                            msg.role === "user"
                              ? "bg-teal-600 text-white rounded-br-sm"
                              : "bg-zinc-50 border border-zinc-100 text-zinc-700 rounded-bl-sm"
                          }`}
                        >
                          {msg.role === "assistant" ? (
                            <div className="relative">
                              <MarkdownLight text={msg.content || ""} />
                              {msg.streaming && (
                                <span className="inline-block w-1.5 h-4 bg-teal-500 animate-pulse ml-0.5 align-middle rounded-sm" />
                              )}
                              {!msg.streaming && msg.content && <CopyButton text={msg.content} />}
                            </div>
                          ) : (
                            msg.content
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Quick prompts — shown only when just the welcome msg */}
                    {messages.length === 1 && (
                      <div className="flex flex-wrap gap-2 mt-2 ml-10">
                        {QUICK_PROMPTS.map((p, i) => (
                          <button
                            key={i}
                            onClick={() => sendMessage(p)}
                            className="text-[11px] px-3 py-1.5 rounded-full border border-teal-200 bg-white text-teal-700 hover:bg-teal-50 hover:border-teal-300 transition-colors"
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    )}

                    {error && (
                      <div className="text-center text-xs text-red-500 bg-red-50 rounded-lg py-2 px-4">
                        {error}
                      </div>
                    )}

                    <div ref={chatEndRef} />
                  </div>

                  {/* Input */}
                  <div className="border-t bg-white p-3">
                    <div className="flex items-end gap-2">
                      <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask about grants, applications, or nonprofit strategy..."
                        rows={1}
                        className="flex-1 resize-none rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm outline-none focus:border-teal-300 focus:bg-white transition-colors placeholder:text-zinc-400"
                        style={{ minHeight: "38px", maxHeight: "100px" }}
                        disabled={isLoading}
                      />
                      <Button
                        onClick={handleSend}
                        disabled={!input.trim() || isLoading}
                        className="h-[38px] px-3 bg-teal-600 hover:bg-teal-700 text-white shrink-0"
                      >
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-[9px] text-zinc-400 mt-1.5 text-center">
                      Powered by Qwen3-235B · Responses may contain inaccuracies · Always verify grant details with the funder
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Sidebar */}
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Copilot Capabilities</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { icon: ShieldCheck, color: "text-green-500", title: "Grant Discovery", desc: "Search and filter Canadian grants by mission, geography, and budget." },
                      { icon: Zap, color: "text-amber-500", title: "Application Guidance", desc: "Step-by-step help with grant applications and compliance." },
                      { icon: MessageSquare, color: "text-blue-500", title: "Session Memory", desc: "Remembers context within your conversation." },
                    ].map((feat, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <feat.icon className={`h-3.5 w-3.5 ${feat.color} mt-0.5 shrink-0`} />
                        <div>
                          <p className="text-[11px] font-semibold text-zinc-800">{feat.title}</p>
                          <p className="text-[9px] text-zinc-500 leading-relaxed">{feat.desc}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200">
                  <CardContent className="p-4 text-center space-y-2">
                    <Bot className="h-8 w-8 text-teal-600 mx-auto" />
                    <h3 className="text-xs font-bold text-teal-900">Try These</h3>
                    <div className="space-y-1">
                      {[
                        "Find housing grants",
                        "T3010 filing help",
                        "Compare federal vs provincial",
                      ].map((s, i) => (
                        <button
                          key={i}
                          onClick={() => { setActiveRoute("chatbot"); sendMessage(s); }}
                          className="block w-full text-[10px] text-left px-2.5 py-1.5 rounded-md bg-white/60 text-teal-700 hover:bg-white hover:shadow-sm border border-teal-100 transition-all"
                        >
                          "{s}"
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeRoute === "api" && (
            <Card>
              <CardContent className="p-12 text-center space-y-6">
                <Code className="h-16 w-16 text-blue-600 mx-auto" />
                <div className="max-w-md mx-auto">
                  <h2 className="text-xl font-bold">Developer API Console</h2>
                  <p className="text-sm text-zinc-500 mt-2">
                    Access our API documentation, generate keys, and test endpoints in the developer sandbox.
                  </p>
                </div>
                <div className="flex justify-center gap-3">
                  <Button variant="outline" className="text-xs">
                    View Docs <ExternalLink className="h-3 w-3 ml-2" />
                  </Button>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
                    Generate API Key
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {activeRoute === "whitelabel" && (
            <Card>
              <CardContent className="p-12 text-center space-y-6">
                <Layers className="h-16 w-16 text-purple-600 mx-auto" />
                <div className="max-w-md mx-auto">
                  <h2 className="text-xl font-bold">White-label Instance</h2>
                  <p className="text-sm text-zinc-500 mt-2">
                    Spin up a branded portal for your clients. Fully customizable with your colors, logo, and messaging.
                  </p>
                </div>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white text-xs">
                  Start Configuration Wizard
                </Button>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AdoptionTab;
